'use client';

import {
  Menu,
  Button,
  Tooltip,
  Skeleton,
  BodyDefault,
  BodyStrong,
  LabelDefault,
} from '@terra/ui-design-system';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Portal } from '@ark-ui/react';
import {
  BookOpen01Icon,
  ArrowDown01Icon,
  LinkSquare02Icon,
  KeyboardIcon,
  Logout01Icon,
  Settings01Icon,
} from '@terra/ui-icons';
import { useRef } from 'react';
import { useTranslations } from 'next-intl';
import { useAuth } from '../../../app/providers/auth';
import { clearCurrentTenantId } from '../../../store/tenantStorage';
import { useMainContentTabs } from '../../../store/layoutStore';
import { useTargetId } from '../../../store/projectStore';
import { MenuDivider } from '../MenuDivider';
import { sidebarTabMapping } from '../utils';
import { AppearanceSubmenu } from './AppearanceSubmenu';
import { ProfileAvatar } from './ProfileAvatar';
import {
  DOCUMENTATION_URL,
  SHORTCUTS_SETTINGS_TAB,
  SIGN_IN_PATH,
  TORCH_SETTINGS_TAB,
} from './constants';
import { setProxySettingsSubTabOnActiveTab } from '../../../utils/setMainContentSettingsSubTab';
import { clearTorchNotSupportedToast } from '../../../hooks/torch-not-supported-toast';

function openDocumentation() {
  if (typeof window === 'undefined') {
    return;
  }

  if (window.electronAPI?.shell) {
    void window.electronAPI.shell.openExternal(DOCUMENTATION_URL);

    return;
  }

  window.open(DOCUMENTATION_URL, '_blank', 'noopener,noreferrer');
}

type ProfileMenuProps = {
  collapsed: boolean;
  scope?: 'workspace' | 'launcher';
};
const MENU_GAP_PX = 4;
const MENU_LEFT_MARGIN_PX = 8;
const VIEWPORT_TOP_MARGIN_PX = 8;
const MENU_HEIGHT_LAYOUT_TIMEOUT_MS = 100;
const waitForFloatingHeight = (element: HTMLElement): Promise<number> => {
  if (element.offsetHeight > 0) {
    return Promise.resolve(element.offsetHeight);
  }

  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) {
        return;
      }

      settled = true;
      observer.disconnect();
      window.clearTimeout(timeoutId);
      resolve(element.offsetHeight);
    };

    const observer = new ResizeObserver(() => {
      if (element.offsetHeight > 0) {
        finish();
      }
    });

    observer.observe(element);
    const timeoutId = window.setTimeout(finish, MENU_HEIGHT_LAYOUT_TIMEOUT_MS);

    requestAnimationFrame(() => {
      if (element.offsetHeight > 0) {
        finish();
      }
    });
  });
};

const triggerInteractionStyles = {
  bg: 'transparent',
  _hover: { bg: 'bg.neutral.hover' },
  _open: {
    bg: 'bg.neutral.subtle',
    _hover: { bg: 'bg.neutral.subtle' },
  },
} as const;

export function ProfileMenu({
  collapsed,
  scope = 'workspace',
}: ProfileMenuProps) {
  const showWorkspaceItems = scope === 'workspace';
  const triggerContainerRef = useRef<HTMLDivElement>(null);
  const t = useTranslations('layout');
  const { user, isLoading, logout } = useAuth();
  const { navigateInActiveTab } = useMainContentTabs();
  const targetId = useTargetId();
  const isProxySettingsDisabled = !targetId;
  const isElectron = typeof window !== 'undefined' && !!window.electronAPI;
  const name = user?.name;
  const email = user?.email;
  const handleLogout = async () => {
    clearTorchNotSupportedToast();
    clearCurrentTenantId();
    if (isElectron) {
      await window.electronAPI.auth.logout();
    } else {
      await logout();
      window.location.href = SIGN_IN_PATH;
    }
  };

  const proxySettingsTab = sidebarTabMapping['proxy-settings'];
  const handleOpenTorchSettings = () => {
    if (isProxySettingsDisabled) {
      return;
    }

    navigateInActiveTab(proxySettingsTab);
    setProxySettingsSubTabOnActiveTab(TORCH_SETTINGS_TAB);
  };

  const handleOpenShortcuts = () => {
    if (isProxySettingsDisabled) {
      return;
    }

    navigateInActiveTab(proxySettingsTab);
    setProxySettingsSubTabOnActiveTab(SHORTCUTS_SETTINGS_TAB);
  };

  if (isLoading) {
    return (
      <Skeleton
        height="36px"
        width="full"
        borderRadius={collapsed ? '0' : 'l1'}
      />
    );
  }

  const displayName = name || email || '';
  const showEmailLine = Boolean(email) && email !== displayName;
  const profileAvatar = (
    <ProfileAvatar name={displayName} picture={user?.picture} />
  );
  const trigger = collapsed ? (
    <Tooltip
      title={displayName}
      openDelay={0}
      positioning={{
        placement: 'right',
        strategy: 'fixed',
        offset: { mainAxis: 12 },
      }}
    >
      <Box display="flex" justifyContent="center" w="full">
        <Menu.Trigger asChild>
          <HStack
            role="button"
            tabIndex={0}
            aria-label={t('profileMenu')}
            w="8"
            h="8"
            justifyContent="center"
            alignItems="center"
            borderRadius="md"
            cursor="pointer"
            {...triggerInteractionStyles}
          >
            {profileAvatar}
          </HStack>
        </Menu.Trigger>
      </Box>
    </Tooltip>
  ) : (
    <Menu.Trigger asChild>
      <Button
        variant="ghost"
        size="sm"
        w="full"
        h="9"
        py="0"
        px="2.5"
        gap="1"
        borderRadius="l1"
        justifyContent="space-between"
        aria-label={t('profileMenu')}
        {...triggerInteractionStyles}
      >
        <HStack gap="1" minW="0" flex="1" alignItems="center">
          {profileAvatar}
          <VStack gap="0" alignItems="flex-start" minW="0" flex="1">
            <BodyDefault as="span" truncate w="full" textAlign="left">
              {displayName}
            </BodyDefault>
            {showEmailLine ? (
              <LabelDefault
                as="span"
                color="fg.neutral.muted"
                truncate
                w="full"
                textAlign="left"
              >
                {email}
              </LabelDefault>
            ) : null}
          </VStack>
        </HStack>
        <Box flexShrink={0} display="flex" alignItems="center">
          <ArrowDown01Icon size={16} />
        </Box>
      </Button>
    </Menu.Trigger>
  );

  return (
    <Box ref={triggerContainerRef} w="full" position="relative">
      <Menu.Root
        lazyMount
        unmountOnExit
        positioning={{
          strategy: 'fixed',
          placement: 'top-start',
          gutter: MENU_GAP_PX,
          flip: false,
          slide: false,
          overlap: false,
          overflowPadding: 0,
          getAnchorRect: () => {
            const trigger = triggerContainerRef.current;

            if (!trigger) {
              return null;
            }

            const rect = trigger.getBoundingClientRect();

            return {
              x: Math.max(rect.x, MENU_LEFT_MARGIN_PX),
              y: rect.y,
              width: rect.width,
              height: rect.height,
            };
          },
          updatePosition: async ({ updatePosition, floatingElement }) => {
            await updatePosition();
            const trigger = triggerContainerRef.current;

            if (!trigger || !floatingElement) {
              return;
            }

            const rect = trigger.getBoundingClientRect();
            const availableHeight = Math.max(
              0,
              Math.round(rect.top - MENU_GAP_PX - VIEWPORT_TOP_MARGIN_PX),
            );

            floatingElement.style.setProperty(
              '--max-h',
              `${availableHeight}px`,
            );
            floatingElement.style.setProperty(
              '--x',
              `${Math.round(Math.max(rect.left, MENU_LEFT_MARGIN_PX))}px`,
            );
            const menuHeight = await waitForFloatingHeight(floatingElement);

            floatingElement.style.setProperty(
              '--y',
              `${Math.round(Math.max(rect.top - MENU_GAP_PX - menuHeight, VIEWPORT_TOP_MARGIN_PX))}px`,
            );
          },
        }}
      >
        {trigger}
        <Portal>
          <Menu.Positioner>
            <Menu.Content
              minW="320px"
              maxW="320px"
              maxH="var(--max-h)"
              p="2"
              gap="1"
              borderRadius="xl"
              borderWidth="1px"
              borderColor="border.neutral.subtle"
              boxShadow="md"
              bg="bg.neutral.default"
              overflowY="auto"
            >
              <Box px="2" py="1">
                <VStack gap="0" alignItems="flex-start" minW="0">
                  <BodyStrong truncate w="full" textAlign="left">
                    {displayName}
                  </BodyStrong>
                  {showEmailLine ? (
                    <LabelDefault
                      as="span"
                      color="fg.neutral.muted"
                      truncate
                      w="full"
                      textAlign="left"
                    >
                      {email}
                    </LabelDefault>
                  ) : null}
                </VStack>
              </Box>
              <MenuDivider />
              {showWorkspaceItems ? (
                <>
                  <Menu.Item
                    value="torch-settings"
                    p="2"
                    disabled={isProxySettingsDisabled}
                    onClick={handleOpenTorchSettings}
                  >
                    <HStack gap="2" w="full" alignItems="center">
                      <Settings01Icon size={14} />
                      <BodyDefault>{t('torchSettings')}</BodyDefault>
                    </HStack>
                  </Menu.Item>
                  <Menu.Item
                    value="shortcuts"
                    p="2"
                    disabled={isProxySettingsDisabled}
                    onClick={handleOpenShortcuts}
                  >
                    <HStack gap="2" w="full" alignItems="center">
                      <KeyboardIcon size={14} />
                      <BodyDefault>{t('shortcuts')}</BodyDefault>
                    </HStack>
                  </Menu.Item>
                </>
              ) : null}
              <AppearanceSubmenu />
              {showWorkspaceItems ? (
                <Menu.Item
                  value="documentation"
                  p="2"
                  onClick={openDocumentation}
                >
                  <HStack gap="2" w="full" alignItems="center">
                    <BookOpen01Icon size={14} />
                    <BodyDefault flexGrow={1}>{t('documentation')}</BodyDefault>
                    <Box
                      w="14px"
                      h="14px"
                      flexShrink={0}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      color="fg.neutral.default"
                    >
                      <LinkSquare02Icon size={14} />
                    </Box>
                  </HStack>
                </Menu.Item>
              ) : null}
              <MenuDivider />
              <Box p="1" w="full">
                <Menu.Item value="logout" p="2" onClick={handleLogout}>
                  <HStack gap="2" w="full" alignItems="center">
                    <Logout01Icon size={14} />
                    <BodyStrong color="fg.neutral.muted">
                      {t('logout')}
                    </BodyStrong>
                  </HStack>
                </Menu.Item>
              </Box>
            </Menu.Content>
          </Menu.Positioner>
        </Portal>
      </Menu.Root>
    </Box>
  );
}
